/**
 * Transaction Type Enumeration
 *
 * Defines all valid transaction types in the system.
 * Values must match Prisma schema enum (lowercase with underscores).
 */
export enum TransactionType {
  // Basic trades
  Buy = 'buy',
  Sell = 'sell',

  // Income
  Dividend = 'dividend',           // Dividend income (cash or reinvested - the income part)
  Reinvestment = 'reinvestment',   // Purchase of shares using reinvested funds
  Interest = 'interest',
  FplIncome = 'fpl_income',        // Fully Paid Lending income
  CapitalGainDistributionLong = 'capital_gain_distribution_long',   // Fund LT cap-gain distribution (1099-DIV 2a)
  CapitalGainDistributionShort = 'capital_gain_distribution_short', // Fund ST cap-gain distribution (taxed as ordinary dividend)

  // Transfers
  TransferIn = 'transfer_in',
  TransferOut = 'transfer_out',
  Deposit = 'deposit',
  Withdrawal = 'withdrawal',
  SweepIn = 'sweep_in',
  SweepOut = 'sweep_out',

  // Corporate actions
  Split = 'split',
  Merger = 'merger',
  Spinoff = 'spinoff',
  Redemption = 'redemption',
  RedemptionCancel = 'redemption_cancel',

  // Options
  OptionAssignment = 'option_assignment',
  OptionExercise = 'option_exercise',
  OptionExpiration = 'option_expiration',
  BuyToOpen = 'buy_to_open',
  BuyToClose = 'buy_to_close',
  SellToOpen = 'sell_to_open',
  SellToClose = 'sell_to_close',

  // Fees & Adjustments
  Fee = 'fee',
  CashAdjustment = 'cash_adjustment',
  Journal = 'journal',
}

/**
 * Transaction action classification sets.
 * Used by cost basis and gains calculation services.
 */

/** Actions that add shares/contracts to a position */
export const ACQUISITION_ACTIONS: readonly TransactionType[] = [
  TransactionType.Buy,
  TransactionType.Reinvestment,
  TransactionType.TransferIn,
  TransactionType.BuyToOpen,
  TransactionType.OptionAssignment,
  TransactionType.Split,      // Can add shares
  TransactionType.Spinoff,    // Adds new position
] as const;

/** Actions that remove shares/contracts from a position */
export const DISPOSITION_ACTIONS: readonly TransactionType[] = [
  TransactionType.Sell,
  TransactionType.TransferOut,
  TransactionType.BuyToClose,      // Closing a short option position
  TransactionType.SellToClose,
  TransactionType.OptionExpiration,
  TransactionType.OptionExercise,
  TransactionType.Redemption,
] as const;

/** Actions that open option positions */
export const OPTION_OPEN_ACTIONS: readonly TransactionType[] = [
  TransactionType.BuyToOpen,
  TransactionType.SellToOpen,
] as const;

/** Actions that close option positions */
export const OPTION_CLOSE_ACTIONS: readonly TransactionType[] = [
  TransactionType.BuyToClose,
  TransactionType.SellToClose,
  TransactionType.OptionExpiration,
  TransactionType.OptionAssignment,
  TransactionType.OptionExercise,
] as const;

/** All option-related actions */
export const OPTION_ACTIONS: readonly TransactionType[] = [
  TransactionType.BuyToOpen,
  TransactionType.BuyToClose,
  TransactionType.SellToOpen,
  TransactionType.SellToClose,
  TransactionType.OptionExpiration,
  TransactionType.OptionAssignment,
  TransactionType.OptionExercise,
] as const;

/** Income-generating actions */
export const INCOME_ACTIONS: readonly TransactionType[] = [
  TransactionType.Dividend,
  TransactionType.CapitalGainDistributionLong,
  TransactionType.CapitalGainDistributionShort,
  TransactionType.Interest,
  TransactionType.FplIncome,
] as const;

/** Actions that affect share quantity (for cost basis transaction history) */
export const SHARE_AFFECTING_ACTIONS: readonly TransactionType[] = [
  TransactionType.Buy,
  TransactionType.Sell,
  TransactionType.Reinvestment,
  TransactionType.OptionAssignment,
  TransactionType.OptionExercise,
  TransactionType.TransferIn,
  TransactionType.TransferOut,
  TransactionType.Split,
  TransactionType.Spinoff,
  TransactionType.Merger,
] as const;

/**
 * Classification functions for transaction types
 */

/** Check if action adds shares/contracts to a position */
export function isAcquisition(action: TransactionType): boolean {
  return (ACQUISITION_ACTIONS as readonly TransactionType[]).includes(action);
}

/** Check if action removes shares/contracts from a position */
export function isDisposition(action: TransactionType): boolean {
  return (DISPOSITION_ACTIONS as readonly TransactionType[]).includes(action);
}

/** Check if action is an option transaction */
export function isOptionTransaction(action: TransactionType): boolean {
  return (OPTION_ACTIONS as readonly TransactionType[]).includes(action);
}

/** Check if action opens an option position */
export function isOptionOpen(action: TransactionType): boolean {
  return (OPTION_OPEN_ACTIONS as readonly TransactionType[]).includes(action);
}

/** Check if action closes an option position */
export function isOptionClose(action: TransactionType): boolean {
  return (OPTION_CLOSE_ACTIONS as readonly TransactionType[]).includes(action);
}

/** Check if action generates income */
export function isIncomeAction(action: TransactionType): boolean {
  return (INCOME_ACTIONS as readonly TransactionType[]).includes(action);
}

/** Check if action affects share quantity */
export function isShareAffecting(action: TransactionType): boolean {
  return (SHARE_AFFECTING_ACTIONS as readonly TransactionType[]).includes(action);
}

/** Check if action is a cash deposit or transfer in */
export function isDeposit(action: TransactionType): boolean {
  return action === TransactionType.Deposit ||
         action === TransactionType.TransferIn ||
         action === TransactionType.SweepIn;
}

/** Check if action is a cash withdrawal or transfer out */
export function isWithdrawal(action: TransactionType): boolean {
  return action === TransactionType.Withdrawal ||
         action === TransactionType.TransferOut ||
         action === TransactionType.SweepOut;
}

/**
 * Check if an action string matches a TransactionType enum value
 */
export function matchesTransactionType(
  action: string | null | undefined,
  type: TransactionType
): boolean {
  if (!action) return false;
  return action.toLowerCase() === type.toLowerCase();
}

/**
 * Normalize action string to TransactionType enum
 * Returns undefined if no match
 */
export function toTransactionType(action: string | null | undefined): TransactionType | undefined {
  if (!action) return undefined;
  const normalized = action.toLowerCase().replace(/\s+/g, '_');
  return Object.values(TransactionType).find(t => t === normalized);
}

/**
 * Human-readable labels for transaction types
 */
const TRANSACTION_TYPE_LABELS: Record<TransactionType, string> = {
  [TransactionType.Buy]: 'Buy',
  [TransactionType.Sell]: 'Sell',
  [TransactionType.Dividend]: 'Dividend',
  [TransactionType.Reinvestment]: 'Reinvestment',
  [TransactionType.Interest]: 'Interest',
  [TransactionType.FplIncome]: 'FPL Income',
  [TransactionType.CapitalGainDistributionLong]: 'LT Capital Gain Distribution',
  [TransactionType.CapitalGainDistributionShort]: 'ST Capital Gain Distribution',
  [TransactionType.TransferIn]: 'Transfer In',
  [TransactionType.TransferOut]: 'Transfer Out',
  [TransactionType.Deposit]: 'Deposit',
  [TransactionType.Withdrawal]: 'Withdrawal',
  [TransactionType.SweepIn]: 'Sweep In',
  [TransactionType.SweepOut]: 'Sweep Out',
  [TransactionType.Split]: 'Split',
  [TransactionType.Merger]: 'Merger',
  [TransactionType.Spinoff]: 'Spinoff',
  [TransactionType.Redemption]: 'Redemption',
  [TransactionType.RedemptionCancel]: 'Redemption Cancel',
  [TransactionType.OptionAssignment]: 'Assignment',
  [TransactionType.OptionExercise]: 'Exercise',
  [TransactionType.OptionExpiration]: 'Expiration',
  [TransactionType.BuyToOpen]: 'Buy to Open',
  [TransactionType.BuyToClose]: 'Buy to Close',
  [TransactionType.SellToOpen]: 'Sell to Open',
  [TransactionType.SellToClose]: 'Sell to Close',
  [TransactionType.Fee]: 'Fee',
  [TransactionType.CashAdjustment]: 'Cash Adjustment',
  [TransactionType.Journal]: 'Journal',
};

/**
 * Get human-readable label for a transaction type
 */
export function getTransactionTypeLabel(action: TransactionType | string | null | undefined): string {
  if (!action) return '-';

  // If it's already a TransactionType enum value
  if (Object.values(TransactionType).includes(action as TransactionType)) {
    return TRANSACTION_TYPE_LABELS[action as TransactionType];
  }

  // Try to convert string to TransactionType
  const txType = toTransactionType(action);
  if (txType) {
    return TRANSACTION_TYPE_LABELS[txType];
  }

  // Fallback: capitalize and format the string
  return action
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
